import styles from '@/components/atoms/ChipMultiSelect/ChipMultiSelect.module.css';
import useSearchLocation, { type LocationType } from '@/hooks/api-hooks/useSearchLocation';
import { useDebounce } from '@/hooks/useDebounce';
import { Combobox, Transition } from '@headlessui/react';
import { Fragment, useCallback, useEffect, useState } from 'react';

type ChipMultiSelectProps = {
  value: LocationType | undefined;
  onChange?: (selectedSkills?: LocationType) => void;
  placeholder?: string;
  disabled?: boolean;
};

export default function LocationDropdown({
  value,
  onChange,
  placeholder,
  disabled,
}: ChipMultiSelectProps) {
  const [query, setQuery] = useState<string>('');
  const debouncedQuery = useDebounce(query, 50);
  const skillsQuery = useSearchLocation(debouncedQuery);
  const [state, setState] = useState<LocationType | undefined>(value);

  useEffect(() => {
    if (value?.id !== state?.id) {
      setState(value);
    } else if (!value) {
      setState(undefined);
      setQuery('');
    }
  }, [value]);

  const _onChange = useCallback(
    (selectedItems: LocationType | null) => {
      setState(selectedItems ?? undefined);
      onChange?.(selectedItems ?? undefined);
      setQuery(selectedItems?.label ?? '');
    },
    [onChange]
  );

  return (
    <Combobox value={state} onChange={_onChange} disabled={disabled}>
      <div className={`${styles.dropdownContainer} ${disabled ? styles.disabled : ''}`}>
        <div className={styles.inputContainer}>
          <Combobox.Input
            className={styles.input}
            placeholder={placeholder}
            aria-disabled={disabled}
            displayValue={(_person) => {
              return state?.label ?? '';
            }}
            onChange={(event) => setQuery(event.target.value)}
          />
          {!!state && (
            <button
              className={styles.clearButton}
              type={'button'}
              disabled={disabled}
              onClick={() => {
                _onChange(null);
              }}
            >
              Clear
            </button>
          )}
          <Combobox.Button className={styles.dropdownButton}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="#667085"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Combobox.Button>
        </div>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setQuery('')}
        >
          <Combobox.Options className={styles.dropdown}>
            {skillsQuery.data?.length === 0 && query !== '' ? (
              <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                Nothing found.
              </div>
            ) : (
              skillsQuery.data?.map((feature) => (
                <Combobox.Option
                  key={feature.id}
                  className={({ active }) =>
                    `${styles.comboBoxOption} ${active ? styles.comboBoxOptionActive : ''}`
                  }
                  value={feature}
                >
                  {({ selected, active }) => (
                    <>
                      <span
                        className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                      >
                        {feature.label}
                      </span>
                      {selected ? (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M16.6668 5L7.50016 14.1667L3.3335 10"
                            stroke="#9B338D"
                            strokeWidth="1.66667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : null}
                    </>
                  )}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </Transition>
      </div>
    </Combobox>
  );
}
